import PropTypes from "prop-types";
import React, { useEffect, useCallback, useRef } from "react";

import qs from "querystring";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Typography, IconButton } from "@material-ui/core";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import styled from "styled-components";
import { fetchMonitoredUri } from "../../actions/monitoredUri";
import DashboardSimple from "../DashboardSimple";
import TableFilters from "./UriTable/TableFilters";
import UriTable from "./UriTable/UriTable";
import CrawlerControlPanel from "../CrawlerControlPanel";
import WorkspacePolling from "./WorkspacePolling";
import WorkspaceHeader from "../common/WorkspaceHeader";


/* ── URL table section ────────────────────────────────────── */
const UrlSection = styled.div`
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    background: #ffffff;
    padding: 20px 24px;
    @media (max-width: 600px) {
        border-radius: 0;
        border-left: none;
        border-right: none;
        padding: 16px 14px;
    }
`;

const UrlSectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
`;

const UrlSectionLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const UrlSectionLabel = styled.div`
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    color: #64748b;
`;

const UrlCount = styled.span`
    font-size: 0.75rem;
    font-weight: 600;
    color: #0f172a;
    margin-left: 8px;
    font-variant-numeric: tabular-nums;
`;


const Workspace = () => {
    const dispatch = useDispatch();
    const history  = useHistory();
    const location = useLocation();
    const { workspaceNumber } = useParams();

    const { tableData, filter, page } = useSelector((state) => state.monitoredUri || {});
    const workspaces = useSelector((state) => state.workspaces || {});
    const ws = workspaces?.current;

    const urlSectionRef = useRef(null);
    const scrollAfterFetchRef = useRef(false);

    useEffect(() => {
        dispatch(fetchMonitoredUri(location.search, workspaceNumber));
    }, [dispatch, location.search, workspaceNumber]);

    // Scroll only *after* the refetch has completed and the section has remounted.
    // Scrolling inside onFilter doesn't survive the in-flight refetch — `tableData` briefly
    // goes null, this component returns null, urlSectionRef unmounts. So onFilter just
    // arms this flag; we trigger the scroll the next time tableData is truthy.
    useEffect(() => {
        if (!scrollAfterFetchRef.current || !tableData || !urlSectionRef.current) return;
        // The table rows render in a later commit than the surrounding section frame, so the
        // page is briefly only viewport-tall when this effect first fires (scrollHeight ≈
        // innerHeight, no room to scroll). Wait for document.scrollHeight to grow past the
        // viewport — that's the signal the table has actually populated — then scroll. Up
        // to ~1.2s, after which we give up rather than spin forever.
        scrollAfterFetchRef.current = false;
        const start = Date.now();
        const tryScroll = () => {
            const el = urlSectionRef.current;
            if (!el) return;
            const canScroll = document.documentElement.scrollHeight > window.innerHeight + 10;
            if (canScroll) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
                return;
            }
            if (Date.now() - start < 1200) requestAnimationFrame(tryScroll);
        };
        requestAnimationFrame(tryScroll);
    }, [tableData]);

    const onFilter = useCallback((qs = "") => {
        scrollAfterFetchRef.current = true;
        history.push(qs || { search: "" });
    }, [history]);

    const handlePageChange = useCallback(
        (evt) => {
            const curQueryString = qs.parse(location.search.replace(/^\?/, ""));
            curQueryString.page = evt.selected;
            history.push("?" + qs.stringify(curQueryString));
        },
        [history, location.search]
    );

    if (!tableData) {
        return null;
    }

    const totalPages = page?.totalElements;

    return (
        <>
            <CrawlerControlPanel />
            {workspaces?.current?.id && (
                <WorkspacePolling workspaceId={workspaces.current.id} />
            )}
            <WorkspaceHeader
                    name={ws?.name}
                    url={ws?.websiteUrl}
                    countryCode={ws?.countryCode}
                />

                <DashboardSimple onFilter={onFilter} />

                <UrlSection ref={urlSectionRef}>
                    <UrlSectionHeader>
                        <UrlSectionLeft>
                            <UrlSectionLabel>
                                URLs
                                {totalPages != null && <UrlCount>({totalPages.toLocaleString()})</UrlCount>}
                            </UrlSectionLabel>
                        </UrlSectionLeft>
                    </UrlSectionHeader>
                    <TableFilters />
                    <UriTable tableData={tableData} filter={filter} />
                    <PaginateLinks
                        pageCount={page?.totalPages ?? 0}
                        forcePage={page?.number ?? 0}
                        onPageChange={handlePageChange}
                    />
                </UrlSection>
        </>
    );
};

/* ── Pagination ──────────────────────────────────────────── */

const PagRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 8px 0;
`;

const PageBtn = styled.button`
    appearance: none;
    border: 1px solid ${({ $active }) => ($active ? "#6366f1" : "#e5e7eb")};
    background: ${({ $active }) => ($active ? "#6366f1" : "#fff")};
    color: ${({ $active }) => ($active ? "#fff" : "#374151")};
    border-radius: 8px;
    min-width: 36px;
    height: 36px;
    padding: 0 10px;
    font-size: 0.85rem;
    font-weight: ${({ $active }) => ($active ? "700" : "500")};
    cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
    opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
    transition: background 120ms ease, border-color 120ms ease, color 120ms ease;
    &:hover:not(:disabled):not([data-active="true"]) {
        background: #f5f3ff;
        border-color: #c7d2fe;
        color: #4f46e5;
    }
`;

const Ellipsis = styled.span`
    width: 36px;
    height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #9ca3af;
    font-size: 0.85rem;
`;

const NavBtn = styled(IconButton)`
    && {
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        width: 36px;
        height: 36px;
        padding: 0;
        background: #fff;
        color: #374151;
        transition: background 120ms, border-color 120ms;
    }
    &&:hover { background: #f5f3ff; border-color: #c7d2fe; color: #4f46e5; }
    &&:disabled { opacity: 0.4; }
`;

function buildPageWindows(current, total, wing = 2) {
    if (total <= 1) return [];
    const pages = [];
    const left = new Set([0, 1]);
    const right = new Set([total - 2, total - 1]);
    for (let i = current - wing; i <= current + wing; i++) {
        if (i >= 0 && i < total) left.add(i), right.add(i);
    }
    const all = [...new Set([...left, ...right])].filter(n => n >= 0 && n < total).sort((a, b) => a - b);
    let prev = -1;
    for (const n of all) {
        if (prev !== -1 && n - prev > 1) pages.push(null);
        pages.push(n);
        prev = n;
    }
    return pages;
}

const PaginateLinks = ({ pageCount, forcePage, onPageChange }) => {
    if (!pageCount || pageCount <= 1) return null;
    const pages = buildPageWindows(forcePage, pageCount);

    return (
        <PagRow>
            <NavBtn
                disabled={forcePage === 0}
                onClick={() => onPageChange({ selected: forcePage - 1 })}
                aria-label="Previous page"
                size="small"
            >
                <ChevronLeftIcon fontSize="small" />
            </NavBtn>

            {pages.map((n, i) =>
                n === null ? (
                    <Ellipsis key={`e${i}`}>…</Ellipsis>
                ) : (
                    <PageBtn
                        key={n}
                        $active={n === forcePage}
                        onClick={() => n !== forcePage && onPageChange({ selected: n })}
                        aria-label={`Page ${n + 1}`}
                        aria-current={n === forcePage ? "page" : undefined}
                    >
                        {n + 1}
                    </PageBtn>
                )
            )}

            <NavBtn
                disabled={forcePage >= pageCount - 1}
                onClick={() => onPageChange({ selected: forcePage + 1 })}
                aria-label="Next page"
                size="small"
            >
                <ChevronRightIcon fontSize="small" />
            </NavBtn>

            <Typography variant="caption" style={{ color: "#9ca3af", marginLeft: 8 }}>
                Page {forcePage + 1} of {pageCount}
            </Typography>
        </PagRow>
    );
};

PaginateLinks.propTypes = {
    pageCount: PropTypes.number,
    forcePage: PropTypes.number,
    onPageChange: PropTypes.func,
};

Workspace.propTypes = { urls: PropTypes.array };
Workspace.defaultProps = { monitoredUrls: [] };

export default Workspace;
